import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MentionsInput, Mention } from 'react-mentions-ts';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { APIgetUsers } from '../../../api/User';
import { APIgetSharesUsersMentions } from '../../../api/Shares';

import Avatar from '../../Avatar';

const EMAIL_FRIENDLY_TRIGGER = /(?:^|\s)(@([\w.-]*(?:@[\w.-]*)*))$/;

const EMOTICONS = [
  { id: '🙂', display: ':)' },
  { id: '🙁', display: ':(' },
  { id: '😄', display: ':D' },
  { id: '😉', display: ';)' },
  { id: '❤️', display: '<3' },
  { id: '😮', display: ':O' },
  { id: '😛', display: ':P' },
  { id: '😎', display: '8)' },
];

const EMOTICON_MAP = EMOTICONS.reduce((acc, { id, display }) => {
  acc[display] = id;
  return acc;
}, {});

const sortedShortcuts = Object.keys(EMOTICON_MAP).sort(
  (a, b) => b.length - a.length,
);

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const emoticonRegex = new RegExp(sortedShortcuts.map(escapeRegExp).join('|'), 'g');

const mentionMarkupRegex = /@?\[[^\]]+\]\(user:[^)]+\)/g;

const replaceEmoticons = (text) => {
  if (!text) {
    return text;
  }

  let transformed = '';
  let lastIndex = 0;

  text.replace(mentionMarkupRegex, (mention, offset) => {
    const plainPart = text.slice(lastIndex, offset);
    transformed += plainPart.replace(emoticonRegex, (match) => EMOTICON_MAP[match] ?? match);
    transformed += mention;
    lastIndex = offset + mention.length;
    return mention;
  });

  transformed += text
    .slice(lastIndex)
    .replace(emoticonRegex, (match) => EMOTICON_MAP[match] ?? match);

  return transformed;
};

const USERS_PAGE_SIZE = 10;

const CommentsMentionsEditor = ({
  commentValue,
  handleCommentChange,
  handleKeyPress,
  inputRefFunc,
  isReply,
  disabled,
  share,
}) => {
  const intl = useIntl();

  const placeholder = isReply ? intl.formatMessage({ defaultMessage: 'Write a reply' }) :
    intl.formatMessage({ defaultMessage: 'Leave a comment' });

  const fetchUsersPage = useCallback((query, context) => {
    const { cursor, signal } = context;
    const trimmedQuery = (query ?? '').trim();
    const currentPage = typeof cursor === 'number' && cursor > 0 ? cursor : 1;

    const requestUsers = () => {
      if (share && share.uuid) {
        return APIgetSharesUsersMentions(share.uuid, {
          search: trimmedQuery,
          page: currentPage,
          per_page: String(USERS_PAGE_SIZE),
          order_by: 'created_at',
          order: 'desc',
          activationStatuses: ['active'],
          mentions: true,
        });
      }

      return APIgetUsers({
        search: trimmedQuery,
        page: currentPage,
        per_page: String(USERS_PAGE_SIZE),
        order_by: 'created_at',
        order: 'desc',
        activationStatuses: ['active'],
        mentions: true,
      }, { signal });
    };

    return requestUsers().then((response) => {
      const { data } = response;
      if (!data) {
        return { items: [], nextCursor: null, hasMore: false };
      }

      const items = data.data.map((user) => ({
        id: user.uuid,
        display: user.name,
        avatar_url: user.avatar_url,
        email: user.email,
      }));
      const total = data.meta?.total;
      const perPage = Number(data.meta?.per_page) || USERS_PAGE_SIZE;
      const hasMoreFromMeta =
        Number.isFinite(Number(data.meta?.current_page))
        && Number.isFinite(Number(data.meta?.last_page))
          ? Number(data.meta.current_page) < Number(data.meta.last_page)
          : null;
      let hasMore = items.length === USERS_PAGE_SIZE;

      if (hasMoreFromMeta !== null) {
        hasMore = hasMoreFromMeta;
      } else if (Number.isFinite(Number(total))) {
        hasMore = currentPage * perPage < Number(total);
      }

      return {
        items,
        nextCursor: hasMore ? currentPage + 1 : null,
        hasMore,
      };
    });
  }, [share]);

  const onMentionsChange = (change) => {
    const transformedValue = replaceEmoticons(change.value);
    handleCommentChange(transformedValue);
  };

  return (
    <div style={{ position: 'relative' }} className="mentions-wrapper">
      <MentionsInput
        value={commentValue}
        onMentionsChange={onMentionsChange}
        onKeyDown={handleKeyPress}
        rows={1}
        placeholder={placeholder}
        classNames={{
          control: 'mentions-control',
          highlighter: 'mentions-highlighter',
          input: 'mentions-input',
          suggestions: 'mentions-suggestions-wrapper',
          loadingIndicator: 'mentions-loading-indicator',
          loadingSpinner: 'mentions-loading-spinner',
          loadingSpinnerElement: 'mentions-loading-spinner-element',
        }}
        suggestionsPlacement="auto"
        anchorMode="left"
        inputRef={inputRefFunc}
        disabled={disabled}
        autoResize={true}
      >
        <Mention
          className="mentions-pill"
          markup="@[__display__](user:__id__)"
          trigger={EMAIL_FRIENDLY_TRIGGER}
          data={fetchUsersPage}
          debounceMs={150}
          appendSpaceOnAdd
          renderSuggestion={(suggestion, _query, highlightedDisplay, _index, focused) => (
            <div
              className={clsx(
                'mentions-suggestion-element',
                focused && 'mentions-suggestion-element--focused',
              )}
            >
              <Avatar
                user={{
                  avatar_url: suggestion.avatar_url,
                  name: suggestion.display,
                }}
                size={20}
              />
              <div className="mentions-suggestion-content">
                <span className="mentions-suggestion-name">
                  {highlightedDisplay}
                </span>
                {' '}
                <span className="mentions-suggestion-email">
                  {`(${suggestion.email})`}
                </span>
              </div>
            </div>
          )}
          renderEmpty={() => (
            <div className="mentions-not-found">
              {intl.formatMessage({ defaultMessage: 'No suggestions found' })}
            </div>
          )}
        />
      </MentionsInput>
    </div>
  );
};

const mapStateToProps = state => ({
  share: state.Shares.share,
});

CommentsMentionsEditor.propTypes = {
  handleCommentChange: PropTypes.func.isRequired,
  handleKeyPress: PropTypes.func.isRequired,
  commentValue: PropTypes.string.isRequired,
  inputRefFunc: PropTypes.func.isRequired,
  isReply: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  share: PropTypes.object,
};

CommentsMentionsEditor.defaultProps = {
  share: null,
};

export default connect(mapStateToProps)(CommentsMentionsEditor);
